import React, { useState, useEffect, useRef } from 'react';
import { setupWebhook } from '../services/api';
import { Webhook, KeyRound, CheckCircle, AlertTriangle, Smartphone, RefreshCw, LogOut, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

// ─── Composant connexion WhatsApp ────────────────────────────
function WhatsAppConnect() {
  const [status, setStatus] = useState(null); // null | 'not_created' | 'open' | 'connecting' | ...
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await API.get('/whatsapp/status');
      setStatus(data.status);
      setPhone(data.phone || null);
      if (data.connected) {
        setQr(null);
        stopPolling();
      }
    } catch {}
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(fetchStatus, 3000);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    fetchStatus();
    return () => stopPolling();
  }, []);

  const handleConnect = async () => {
    setLoading(true);
    setQr(null);
    try {
      const { data } = await API.post('/whatsapp/connect');
      if (data.already_connected) {
        toast.success('Déjà connecté !');
        fetchStatus();
        return;
      }
      if (data.qr) {
        setQr(data.qr);
        setStatus('awaiting_scan');
        startPolling();
        toast('Scannez le QR code avec WhatsApp', { icon: '📱' });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur connexion');
    } finally { setLoading(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Déconnecter WhatsApp ?')) return;
    try {
      await API.delete('/whatsapp/disconnect');
      setStatus('close');
      setQr(null);
      setPhone(null);
      stopPolling();
      toast.success('WhatsApp déconnecté');
    } catch { toast.error('Erreur déconnexion'); }
  };

  const isConnected = status === 'open';

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: isConnected ? 'rgba(34,197,94,0.12)' : 'rgba(79,124,255,0.12)'
        }}>
          <Smartphone size={20} color={isConnected ? 'var(--green)' : 'var(--accent)'} />
        </div>
        <div>
          <h2 style={{ fontSize: 17 }}>Connexion WhatsApp</h2>
          <p className="text-sm text-muted">Liez un numéro WhatsApp pour l'envoi des invitations</p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {isConnected
            ? <span className="badge badge-green"><CheckCircle size={11} /> Connecté</span>
            : status === 'awaiting_scan'
            ? <span className="badge badge-amber"><Loader size={11} /> En attente de scan</span>
            : <span className="badge badge-gray">Déconnecté</span>
          }
        </div>
      </div>

      {isConnected && phone && (
        <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--green)' }}>
          ✅ Connecté en tant que <strong>{phone}</strong>
        </div>
      )}

      {/* QR Code */}
      {qr && !isConnected && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
            Ouvrez WhatsApp → <strong style={{ color: 'var(--text)' }}>Appareils liés</strong> → <strong style={{ color: 'var(--text)' }}>Lier un appareil</strong> → scanner ce QR
          </p>
          <div style={{ display: 'inline-block', padding: 16, background: '#fff', borderRadius: 12 }}>
            <img
              src={qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`}
              alt="QR WhatsApp"
              style={{ width: 220, height: 220, display: 'block' }}
            />
          </div>
          <p className="text-sm text-muted" style={{ marginTop: 12 }}>
            <Loader size={12} style={{ display: 'inline', marginRight: 5 }} />
            Détection automatique du scan...
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        {!isConnected && (
          <button className="btn btn-primary" onClick={handleConnect} disabled={loading}>
            {loading ? <span className="spinner" /> : <Smartphone size={15} />}
            {status === 'awaiting_scan' ? 'Actualiser le QR' : 'Connecter WhatsApp'}
          </button>
        )}
        {qr && !isConnected && (
          <button className="btn btn-ghost" onClick={handleConnect} disabled={loading}>
            <RefreshCw size={14} /> Nouveau QR
          </button>
        )}
        {isConnected && (
          <button className="btn btn-danger btn-sm" onClick={handleDisconnect}>
            <LogOut size={14} /> Déconnecter
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Page principale ─────────────────────────────────────────
export default function SettingsPage() {
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const handleWebhook = async () => {
    setWebhookLoading(true);
    setWebhookResult(null);
    try {
      const { data } = await setupWebhook();
      setWebhookResult({ ok: true, url: data.webhookUrl });
      toast.success('Webhook configuré');
    } catch (err) {
      setWebhookResult({ ok: false, msg: err.response?.data?.error || 'Erreur' });
      toast.error('Erreur configuration webhook');
    } finally { setWebhookLoading(false); }
  };

  const handlePassword = async e => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) return toast.error('Les mots de passe ne correspondent pas');
    if (pwForm.newPassword.length < 8) return toast.error('Minimum 8 caractères');
    setPwLoading(true);
    try {
      await API.post('/auth/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword
      });
      toast.success('Mot de passe modifié');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally { setPwLoading(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Paramètres</h1>
          <p className="text-muted">Configuration du système</p>
        </div>
      </div>

      <WhatsAppConnect />

      {/* Webhook */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(79,124,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Webhook size={20} color="var(--accent)" />
          </div>
          <div>
            <h2 style={{ fontSize: 17 }}>Webhook WhatsApp</h2>
            <p className="text-sm text-muted">À configurer après chaque connexion d'un nouveau numéro</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleWebhook} disabled={webhookLoading}>
          {webhookLoading ? <span className="spinner" /> : <Webhook size={15} />}
          Configurer le webhook
        </button>
        {webhookResult && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 'var(--radius)',
            background: webhookResult.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
            border: `1px solid ${webhookResult.ok ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 13
          }}>
            {webhookResult.ok
              ? <><CheckCircle size={15} color="var(--green)" /><span style={{ color: 'var(--green)' }}>Webhook configuré : <code>{webhookResult.url}</code></span></>
              : <><AlertTriangle size={15} color="var(--red)" /><span style={{ color: 'var(--red)' }}>{webhookResult.msg}</span></>
            }
          </div>
        )}
      </div>

      {/* Mot de passe */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(124,92,252,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <KeyRound size={20} color="var(--accent2)" />
          </div>
          <div>
            <h2 style={{ fontSize: 17 }}>Changer le mot de passe</h2>
            <p className="text-sm text-muted">Sécurisez votre accès admin</p>
          </div>
        </div>
        <form onSubmit={handlePassword} style={{ maxWidth: 400 }}>
          <div className="form-group">
            <label>Mot de passe actuel</label>
            <input type="password" value={pwForm.currentPassword} onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Nouveau mot de passe</label>
            <input type="password" value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="Minimum 8 caractères" />
          </div>
          <div className="form-group">
            <label>Confirmer</label>
            <input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={pwLoading}>
            {pwLoading ? <span className="spinner" /> : 'Modifier le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );
}
